import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Box,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Translate } from '../../shared/LanguageService';
import { UserRole } from '../../components/common/header/photo/head-bar/newsComponent/utils/userRoleHelper';
import { INewsItem } from './NewsItem';
import NewsEditorModalForm, { BrokerChain, NewsEditorModalLabels } from './NewsEditorModalForm';

type ExternalProps = {
    open: boolean;
    onClose: () => void;
    roles: { label: string; value: UserRole }[];
    brokerChains: BrokerChain[];
    brokerChainsLoading: boolean;
    onPublish: (item: INewsItem, brokerChainIds: number[]) => Promise<void>;
    onSaveDraft: (item: INewsItem, brokerChainIds: number[]) => Promise<void>;
    editingNews?: INewsItem | null;
};

type NewsEditorModalProps = ExternalProps & {
    labels: NewsEditorModalLabels;
};

function NewsEditorModal({
    open,
    onClose,
    roles,
    brokerChains,
    brokerChainsLoading,
    labels,
    onSaveDraft,
    onPublish,
    editingNews
}: NewsEditorModalProps) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontWeight: 600,
                    pb: 1
                }}
            >
                {editingNews ? labels.editNewsTitle : labels.addNewsTitle}
                <IconButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                {brokerChainsLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <NewsEditorModalForm
                        key={editingNews ? `edit-${editingNews.id}` : 'create'}
                        onClose={onClose}
                        roles={roles}
                        brokerChains={brokerChains}
                        brokerChainsLoading={brokerChainsLoading}
                        labels={labels}
                        onSaveDraft={onSaveDraft}
                        onPublish={onPublish}
                        editingNews={editingNews}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function NewsEditorModalTranslationLayer(props: ExternalProps) {
    let labels: NewsEditorModalLabels = {
        addNewsTitle: Translate('news-editor-modal/add-news-title'),
        titleLabel: Translate('news-editor-modal/title-label'),
        contentLabel: Translate('news-editor-modal/content-label'),
        recipientsLabel: Translate('news-editor-modal/recipients-label'),
        selectRecipientsPlaceholder: Translate('news-editor-modal/select-recipients-placeholder'),
        brokerChainsLabel: Translate('news-editor-modal/broker-chains-label'),
        brokerChainsPlaceholder: Translate('news-editor-modal/broker-chains-placeholder'),
        startDatePicker: Translate('news-editor-modal/start-date-picker'),
        visibleDatePicker: Translate('news-editor-modal/visible-date-picker'),
        endDatePicker: Translate('news-editor-modal/end-date-picker'),
        saveDraftButton: Translate('news-editor-modal/save-draft-button'),
        publishNewsButton: Translate('news-editor-modal/publish-news-button'),
        brokerChainsSelectAllLabel: Translate('news-editor-modal/all-broker-chains'),
        insertLinkTitle: Translate('news-editor-modal/insert-link-title'),
        insertLinkTextPlaceholder: Translate('news-editor-modal/insert-link-text-placeholder'),
        insertLinkUrlPlaceholder: Translate('news-editor-modal/insert-link-url-placeholder'),
        insertLinkButton: Translate('news-editor-modal/insert-link-button'),
        archiveButton: Translate('news-editor-modal/archive-button'),
        unarchiveButton: Translate('news-editor-modal/unarchive-button'),
        previewButton: Translate('news-editor-modal/preview-button'),
        contentWidthHint: Translate('news-editor-modal/content-width-hint'),
        startDateHint: Translate('news-editor/start-date-hint'),
        visibleDateHint: Translate('news-editor/visible-date-hint'),
        endDateHint: Translate('news-editor/end-date-hint'),
        optionalLabel: Translate('news-editor-modal/optional'),
        scheduleNewsButton: Translate('news-editor-modal/schedule-news-button'),
        sectionContent: Translate('news-editor-modal/section-content'),
        sectionRecipients: Translate('news-editor-modal/section-recipients'),
        sectionSchedule: Translate('news-editor-modal/section-schedule'),
        editNewsTitle: Translate('news-editor-modal/edit-news-title')
    };

    return <NewsEditorModal {...props} labels={labels} />;
}
